import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { useCredits } from '@/contexts/CreditsContext';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/Header';
import HelpBenefits, { HELP_URLS } from '@/components/HelpBenefits';
import AdminHiddenSectionsOverview from '@/components/AdminHiddenSectionsOverview';
import FeatureVideoHero from '@/components/FeatureVideoHero';
import ModuleExamplesBar from '@/components/ModuleExamplesBar';
import UpdateCuratedExampleButton from '@/components/UpdateCuratedExampleButton';
import ReportGlossary from '@/components/ReportGlossary';
import { useSortFilterBar } from '@/components/SortFilterBar';
import { useCuratedExamples } from '@/hooks/useCuratedExamples';
import { useApplySpeciesDefaults } from '@/hooks/useSpeciesDefaults';
// PRD final-pass UX simplification (#7): WingmanTriggerPrompts intentionally
// not imported on Snapshot. The contextual "Ask Wingman" prompt pills live
// only near the Growth-vs-Model chart (Spreadsheet + Growth Model pages).
import WingmanInsight from '@/components/WingmanInsight';
import WingmanNudge from '@/components/WingmanNudge';
import FCRVersionGuidanceNote from '@/components/FCRVersionGuidanceNote';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Save, Trash2, ChevronDown, ChevronUp, Clock, FolderOpen, ArrowLeft, Camera, AlertTriangle, CheckCircle, AlertCircle, Search, ArrowUpCircle, ArrowDownCircle, Info, Download, Target, Pencil, Check, X as XIcon, Eye, AlertOctagon, RotateCcw, Lock, ArrowRight } from 'lucide-react';
import { InlineRename } from '@/components/InlineRename';
import { SUPPORTED_SPECIES, COMING_SOON_SPECIES, isTilapiaSpecies, isSeabassSpecies, COMING_SOON_MESSAGE } from '@/config/species';
import { classifySnapshotDeviation } from '@/utils/stockIntelligence';
import { resolveDecisionState } from '@/utils/decisionState';
import { toCanonicalConfidenceLabel, formatMissingFishRange, CONFIDENCE_LABELS } from '@/utils/reportFormatters';
import { SnapshotStockIntegrity } from '@/components/StockIntegrityScore';
import { MissingDataExplanation, InlineMissingData } from '@/components/MissingDataExplanation';
import jsPDF from 'jspdf';

// ============================================================================
// SNAPSHOT DECISION PANEL — Structured decision support for Snapshot Analysis
// 
// GLOBAL POLICY: Snapshot results inform planning and operational review.
// Single snapshots CANNOT confirm stock changes. Resampling required.
// 
// MISUSE RESISTANCE:
// - Single snapshots can NEVER be ACTIONABLE
// - Financial outputs not shown (no feed data in Snapshot)
// - VALIDATION_REQUIRED appears visually stronger than MONITOR_ONLY
// - Downgrade explanation always visible
// ============================================================================

const SnapshotDecisionPanel = ({ missingFishAnalysis, fcrDeviationAnalysis }) => {
  if (!missingFishAnalysis) return null;

  const deviationPercent = missingFishAnalysis.deviation_percent || 0;
  const fishDiff = missingFishAnalysis.fish_difference || 0;
  const interpretation = missingFishAnalysis.interpretation || '';

  // ══════════════════════════════════════════════════════════════════════════
  // 1. DETECTION
  //
  //    ★ PRD — confidence badges MUST use the canonical five-label vocabulary:
  //      High / Moderate / Low / Indication Only / Uncertain
  //
  //    Single-snapshot inputs carry only one data point, so even a "high deviation"
  //    signal is `Indication Only` until a second sample confirms it. The classic
  //    'Low' fallback used here previously violated the PRD — any single snapshot
  //    that clears the 5% deviation floor is now stamped Indication Only, not Low.
  // ══════════════════════════════════════════════════════════════════════════
  let detectionType = 'Normal';
  let strength = CONFIDENCE_LABELS.INDICATION_ONLY;
  let probability = null;

  if (Math.abs(deviationPercent) >= 15) {
    detectionType = fishDiff < 0 ? 'Missing Fish' : 'Extra Fish';
    strength = CONFIDENCE_LABELS.INDICATION_ONLY;  // Snapshot caps at Indication Only regardless of magnitude
    probability = 85;
  } else if (Math.abs(deviationPercent) >= 10) {
    detectionType = fishDiff < 0 ? 'Missing Fish' : 'Extra Fish';
    strength = CONFIDENCE_LABELS.INDICATION_ONLY;
    probability = 65;
  } else if (Math.abs(deviationPercent) >= 5) {
    detectionType = fishDiff < 0 ? 'Missing Fish' : 'Extra Fish';
    strength = CONFIDENCE_LABELS.INDICATION_ONLY;
    probability = 45;
  }
  // Resolve to canonical label (kept as a single source of truth for any
  // downstream surface that reads `strength` directly — e.g. PDF export).
  const canonicalStrength = toCanonicalConfidenceLabel(strength, 35 /* snapshot reliability */, {
    isSingleSnapshot: true,
  });

  // ══════════════════════════════════════════════════════════════════════════
  // 2. DATA RELIABILITY (Snapshot-specific: single point = insufficient)
  // ══════════════════════════════════════════════════════════════════════════
  const reliabilityScore = 35; // Single snapshot = insufficient evidence
  
  // Reliability classification (mapped from score)
  const reliabilityClassification = { label: 'Insufficient evidence', tier: 'insufficient' };
  
  // Reliability drivers for Snapshot
  const reliabilityDrivers = [
    { factor: 'Sample count', status: 'low', detail: 'Single snapshot' },
    { factor: 'Trend data', status: 'low', detail: 'None available' },
    { factor: 'Confirmation', status: 'low', detail: 'Unverified' },
  ];
  
  const sensitivityNote = 'Single snapshot. If sample error > ±5%, conclusion changes.';

  // ══════════════════════════════════════════════════════════════════════════
  // 3. DECISION CLASS — SINGLE SOURCE OF TRUTH
  // ══════════════════════════════════════════════════════════════════════════
  // ★ Routes through resolveDecisionState() — the same function used by
  //   generateIntelligence.js for Growth vs Model and Spreadsheet. Snapshot
  //   passes isSnapshot=true, which caps the result at VALIDATION_REQUIRED.
  const snapshotDecision = resolveDecisionState({
    reliabilityScore,
    detectionStrength: strength,
    hasConflict: false,
    hasSuspectedError: false,
    highVolatility: false,
    hasFeedData: false,
    sampleCount: 1,
    isSnapshot: true,
    hasMissingFish: detectionType === 'Missing Fish',
  });
  const decisionClass = snapshotDecision.decisionClass;
  const decisionReason = snapshotDecision.reason;
  const downgradeReasons = snapshotDecision.downgradeReasons;
  const downgradeExplanation = `Reduced confidence due to: ${downgradeReasons.join(', ').toLowerCase()}`;

  // ══════════════════════════════════════════════════════════════════════════
  // RANGE CALCULATION (never show exact numbers for non-High confidence)
  // ══════════════════════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════════════════════
  // RANGE CALCULATION — use the shared PRD helper so Snapshot, Growth-vs-Model,
  // Spreadsheet and PDF renders all emit the same "Missing: X–Y" / "Extra: X–Y"
  // canonical band. `formatMissingFishRange` also handles the "Within margin"
  // case when the count falls inside the guardrail.
  // ══════════════════════════════════════════════════════════════════════════
  const absDiff = Math.abs(fishDiff);
  const snapshotRange = formatMissingFishRange({
    missingFishCount: absDiff,
    dataReliabilityScore: 35,  // Snapshot reliability is always 35 — single point
    direction: fishDiff < 0 ? 'missing' : fishDiff > 0 ? 'extra' : 'within_margin',
    allowWidening: true,       // Snapshot widens the band to ±15–20% — single-point + no-feed should not appear precision-grade.
  });
  const rangeText = snapshotRange.shortLabel;
  const rangeLow = Math.max(0, Math.round(absDiff * 0.7));   // legacy — some tile renders still use raw bounds
  const rangeHigh = Math.round(absDiff * 1.3);

  // ══════════════════════════════════════════════════════════════════════════
  // 4. PRIMARY ACTION (no operational recommendations for single snapshot)
  // ══════════════════════════════════════════════════════════════════════════
  // Confidence is always Low for single snapshot → NO operational actions
  const primaryAction = {
    text: 'No operational recommendations at this confidence level. Schedule a follow-up sample for trend confirmation.',
    reversibility: 'N/A',
    priority: 'PRIMARY'
  };
  
  // No secondary actions for single snapshot (no causes, no explanations)
  const secondaryActions = [];

  // ══════════════════════════════════════════════════════════════════════════
  // 5. STOCK RECORD POLICY (GLOBAL)
  // ══════════════════════════════════════════════════════════════════════════
  const stockRecordPolicy = {
    canModifyStockRecords: false,
    message: 'Do NOT adjust stock records from single snapshot.',
    detail: 'Snapshot results inform planning only. Stock changes require trend confirmation or physical validation.',
  };

  // ══════════════════════════════════════════════════════════════════════════
  // 6. VALIDATION (always required for non-normal detection)
  // ══════════════════════════════════════════════════════════════════════════
  const needsValidation = detectionType !== 'Normal';
  const validation = needsValidation ? {
    required: true,
    triggers: ['Single snapshot only', 'No trend data', 'Insufficient evidence'],
    examples: ['Follow-up weight sample in 10–14 days', 'Camera/visual inspection'],
  } : {
    required: false,
    triggers: [],
    examples: [],
  };

  // ══════════════════════════════════════════════════════════════════════════
  // STYLING (MISUSE RESISTANCE: VALIDATION_REQUIRED visually stronger)
  // ══════════════════════════════════════════════════════════════════════════
  const classStyles = {
    'ACTIONABLE': { 
      bg: 'bg-gray-50', 
      border: 'border-cyan-400', 
      text: 'text-cyan-700', 
      headerBg: 'bg-cyan-100',
      icon: CheckCircle 
    },
    'MONITOR_ONLY': { 
      bg: 'bg-amber-50', 
      border: 'border-amber-400', 
      text: 'text-amber-700',
      headerBg: 'bg-amber-100',
      icon: Eye 
    },
    'VALIDATION_REQUIRED': { 
      bg: 'bg-red-50', 
      border: 'border-red-500 border-2', 
      text: 'text-red-700',
      headerBg: 'bg-red-100',
      icon: AlertOctagon 
    },
  };
  const style = classStyles[decisionClass] || classStyles['MONITOR_ONLY'];
  const DecisionIcon = style.icon;

  const detectionStyles = {
    'Missing Fish': { color: 'text-amber-700', bg: 'bg-amber-100' },
    'Extra Fish': { color: 'text-blue-700', bg: 'bg-blue-100' },
    'Normal': { color: 'text-green-700', bg: 'bg-green-100' },
  };
  const detStyle = detectionStyles[detectionType] || detectionStyles['Normal'];

  // Driver status colors
  const driverStatusColors = {
    good: 'text-green-600',
    moderate: 'text-amber-600',
    low: 'text-red-600',
  };

  return (
    <div className={`${style.bg} border ${style.border} rounded-lg overflow-hidden mb-4`} data-testid="snapshot-decision-panel">
      {/* Decision Class Header — visually prominent */}
      <div className={`${style.headerBg} px-4 py-2.5 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <DecisionIcon className={`w-5 h-5 ${style.text}`} />
          <span className={`text-sm font-bold ${style.text} tracking-wide`}>
            {decisionClass.replace(/_/g, ' ')}
          </span>
        </div>
        <span className="text-[10px] text-gray-500 max-w-[45%] text-right leading-tight">{decisionReason}</span>
      </div>

      {/* Downgrade explanation banner (always visible for Snapshot) */}
      <div className="bg-amber-100 border-b border-amber-300 px-4 py-2">
        <p className="text-[10px] text-amber-700 font-medium">
          ⚠ {downgradeExplanation}
        </p>
      </div>

      {/* Main content */}
      <div className="p-4">
        {/* Grid: Detection | Reliability | Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          {/* 1. DETECTION */}
          <div className={`${detStyle.bg} rounded-lg p-2.5`}>
            <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">Detection</p>
            <p className={`font-bold ${detStyle.color}`} data-testid="snapshot-detection-type">{detectionType}</p>
            {/* Confidence badge — for Snapshot we no longer surface the raw
                probability number alongside the strength label. The same
                reading appears in the next column as "DATA RELIABILITY xx/100"
                and showing both side-by-side caused customer confusion
                (e.g. "Indication Only (65%)" vs "35/100 Insufficient
                evidence" feel contradictory). Snapshot caps at "Indication
                Only" anyway, so we just say "Indicative signal only" when
                that's the active label. */}
            <p className="text-gray-600 mt-0.5" data-testid="snapshot-confidence-badge">
              {canonicalStrength === 'Indication Only'
                ? 'Indicative signal only'
                : `${canonicalStrength}${probability ? ` (${probability}%)` : ''}`}
            </p>
            {detectionType !== 'Normal' ? (
              <>
                <p className="text-[10px] text-gray-500 mt-1 leading-tight">
                  Expected ~{snapshotRange.midpointLabel} · Range (±{snapshotRange.uncertaintyMarginPercent || 15}%): {snapshotRange.shortLabel} ({deviationPercent > 0 ? '+' : ''}{deviationPercent}%)
                </p>
                {/* Sample-error sensitivity note moved up next to "Single
                    snapshot — treat as indicative" so users see it before
                    they read the deviation number. */}
                <p className="text-[10px] text-amber-700 mt-1 leading-tight">
                  Single snapshot — treat as indicative. If sample error &gt; ±5%, interpretation may materially change.
                </p>
              </>
            ) : (
              <p className="text-[10px] text-gray-500 mt-1 leading-tight">
                {interpretation} ({deviationPercent > 0 ? '+' : ''}{deviationPercent}%)
              </p>
            )}
          </div>

          {/* 2. DATA RELIABILITY (with classification) */}
          <div className="bg-gray-100 rounded-lg p-2.5">
            <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">Data Reliability</p>
            {/* Score + Classification label */}
            <div className="flex items-baseline gap-2">
              <span className="font-bold text-red-600">{reliabilityScore}/100</span>
              <span className="text-[9px] text-red-600">{reliabilityClassification.label}</span>
            </div>
            {/* Reliability drivers */}
            <div className="mt-1.5 space-y-0.5">
              {reliabilityDrivers.map((driver, i) => (
                <div key={i} className="flex items-center justify-between text-[9px]">
                  <span className="text-gray-500">{driver.factor}</span>
                  <span className={driverStatusColors[driver.status]}>{driver.detail}</span>
                </div>
              ))}
            </div>
            <p className="text-[9px] text-amber-600 mt-1.5 leading-tight border-t border-gray-200 pt-1">{sensitivityNote}</p>
          </div>

          {/* 3. ACTIONS (with PRIMARY ACTION prominent) */}
          <div className="bg-gray-100 rounded-lg p-2.5">
            <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-1">Actions</p>
            {/* PRIMARY ACTION */}
            <div className="flex items-start gap-1.5 mb-2 pb-1.5 border-b border-gray-200">
              <ArrowRight className="w-3.5 h-3.5 text-cyan-600 mt-0.5 shrink-0" />
              <span className="text-gray-800 leading-tight text-[10px] font-medium">{primaryAction.text}</span>
            </div>
            {/* Secondary actions */}
            {secondaryActions.length > 0 && (
              <ul className="space-y-1">
                {secondaryActions.map((action, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <RotateCcw className="w-2.5 h-2.5 text-green-600/70 mt-0.5 shrink-0" />
                    <span className="text-gray-600 leading-tight text-[9px]">{action.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Stock Record Policy Banner (GLOBAL — always visible) */}
        <div className="mt-3 bg-gray-200 border border-gray-300 rounded-lg p-2.5 flex items-start gap-2">
          <Lock className="w-4 h-4 text-gray-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] font-semibold text-gray-700">{stockRecordPolicy.message}</p>
            <p className="text-[9px] text-gray-500 mt-0.5">{stockRecordPolicy.detail}</p>
          </div>
        </div>

        {/* Validation Banner (if required) */}
        {validation.required && (
          <div className="mt-2 bg-red-100 border border-red-300 rounded-lg p-2.5 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-red-700">Field Validation Required</p>
              <p className="text-[10px] text-red-600 mt-0.5">
                Triggers: {validation.triggers.join(' · ')}
              </p>
              <p className="text-[10px] text-gray-600 mt-0.5">
                Examples: {validation.examples.join(' · ')}
              </p>
            </div>
          </div>
        )}

        {/* Report Audit Footer — traceability for isolated outputs */}
        <div className="mt-3 pt-2 border-t border-gray-300 flex flex-wrap items-center justify-between text-[8px] text-gray-500 font-mono">
          <span>Detection: {detectionType} ({canonicalStrength})</span>
          <span>Reliability: {reliabilityScore}/100</span>
          <span>Class: {decisionClass}</span>
          <span>Generated: {new Date().toISOString().slice(0, 16).replace('T', ' ')}</span>
        </div>
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SnapshotAnalysis() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { formatCostLabel } = useCredits();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const formRef = useRef(null);
  // Set to true by `loadExampleAndRun` and consumed (read + reset) inside
  // `handleSubmit` so curated "Try [Species]" runs can POST with
  // `?example=1` — which the backend treats as quota-free.
  const exampleRunRef = useRef(false);
  // Tracks the curated example just loaded so the admin "Update Try-X" CTA
  // can render. Cleared whenever a different (non-curated) record loads.
  const [loadedCurated, setLoadedCurated] = useState(null);
  
  // Missing Fish Analysis state
  const [missingFishAnalysis, setMissingFishAnalysis] = useState(null);
  const [showMissingFishAnalysis, setShowMissingFishAnalysis] = useState(false);
  const [fcrDeviationAnalysis, setFcrDeviationAnalysis] = useState(null);
  
  // Save/Load state
  const [savedRecords, setSavedRecords] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [activeRecordId, setActiveRecordId] = useState(null);

  // Excel-style sort + multi-select filter over saved snapshots.
  const snapshotRecordColumns = useMemo(() => ([
    { key: 'name',         label: 'Name',       accessor: r => r.name,                                  type: 'text' },
    { key: 'created_at',   label: 'Created',    accessor: r => r.created_at,                            type: 'date' },
    { key: 'fish_diff',    label: 'Fish Diff',  accessor: r => r.result?.analysis?.fish_difference,     type: 'number' },
    { key: 'species',      label: 'Species',    accessor: r => r.input?.species || r.species,           type: 'text' },
  ]), []);
  const { processedRecords: filteredSnapshotRecords, bar: snapshotSortFilterBar } = useSortFilterBar(
    savedRecords,
    snapshotRecordColumns,
    'snapshot-records',
  );

  // Curated "Try [Species]" examples — admin-controlled via the AdminConsole
  // > Curated Examples panel (replaces the old hardcoded moduleExamples.js).
  const { examples: snapshotExamples, refetch: refetchSnapshotExamples } = useCuratedExamples('snapshot');

  const isDemo = user?.is_demo === true;
  
  // Read-only mode: user not approved (pending admin approval)
  const isReadOnly = !user?.is_approved && !user?.is_unlimited && !user?.is_admin;

  const fetchRecords = async () => {
    try {
      const t = token; // from useAuth
      const res = await axios.get(`${BACKEND_URL}/api/snapshot-records`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setSavedRecords(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchRecords(); }, []);

  const handleSave = async () => {
    if (!result) { toast.error('No result to save'); return; }
    const name = saveName.trim() || `${formData.unit || formData.species} ${formData.input_date || ''}`.trim();
    setSaving(true);
    try {
      const t = token; // from useAuth
      await axios.post(`${BACKEND_URL}/api/snapshot-records`, {
        name,
        species: formData.species,
        form_data: formData,
        result,
        missing_fish_analysis: missingFishAnalysis,
      }, { headers: { Authorization: `Bearer ${t}` } });
      toast.success('Snapshot record saved');
      setSaveName('');
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const loadRecord = (record) => {
    if (record.form_data) setFormData(record.form_data);
    if (record.result) setResult(record.result);
    if (record.missing_fish_analysis) {
      setMissingFishAnalysis(record.missing_fish_analysis);
      setShowMissingFishAnalysis(true);
    }
    setActiveRecordId(record.id);
    setLoadedCurated(null);  // loading a saved record from the panel resets the curated context
    toast.success(`Loaded: ${record.name}`);
  };

  const confirmDelete = (id) => {
    setPendingDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      const t = token; // from useAuth
      await axios.delete(`${BACKEND_URL}/api/snapshot-records/${pendingDeleteId}`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      toast.success('Record deleted');
      if (activeRecordId === pendingDeleteId) setActiveRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
    setPendingDeleteId(null);
  };

  const renameSnapshotRecord = async (recordId, newName) => {
    const t = token; // from useAuth
    await axios.patch(
      `${BACKEND_URL}/api/snapshot-records/${recordId}/rename`,
      { name: newName },
      { headers: { Authorization: `Bearer ${t}` } }
    );
    setSavedRecords(prev => prev.map(r => r.id === recordId ? { ...r, name: newName } : r));
    toast.success('Record renamed');
  };
  
  const [formData, setFormData] = useState({
    species: 'Sparus aurata',
    farm_location: 'Greece',
    genetic_origin: 'Greek',
    batch_code: '',
    unit: '',
    input_date: '',
    input_average_weight_g: '',
    observed_average_weight_g: '',
    expected_fish_count: '',
    observed_biological_fcr: '',
    fcr_version_id: '',   // Phase B: FCR origin version (primarily used for Tilapia)
  });

  const [userSpecies, setUserSpecies] = useState([]);
  // Phase B: Tilapia FCR origin + versions (loaded from Admin Console seeds)
  const [fcrOrigins, setFcrOrigins] = useState([]);

  useEffect(() => {
    const fetchUserSpecies = async () => {
      try {
        const authToken = token; // from useAuth
        const resp = await axios.get(`${BACKEND_URL}/api/user-species`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUserSpecies(resp.data.species || []);
      } catch { /* fallback */ }
    };
    fetchUserSpecies();
  }, []);

  // Phase B: fetch FCR origins (for the Tilapia version selector / display)
  useEffect(() => {
    const fetchFcrOrigins = async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/fcr-origins`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setFcrOrigins(resp.data?.origins || []);
      } catch { /* non-fatal */ }
    };
    fetchFcrOrigins();
  }, []);

  // Phase B: auto-pick the Tilapia/Seabass default version when that species is selected.
  //
  // Bug fix (May 2026): the previous guard `!formData.fcr_version_id`
  // only fired when the field was empty. Switching Tilapia → Seabass left
  // the Tilapia version UUID in place, silently running the analysis with
  // the wrong species table. Same fix as GrowthModelAnalysis.js: consider
  // the new species' default ONLY when the current fcr_version_id does
  // not belong to any version of the new origin.
  useEffect(() => {
    if (!fcrOrigins.length) return;
    let targetCode = null;
    if (isTilapiaSpecies(formData.species)) targetCode = 'tilapia';
    else if (isSeabassSpecies(formData.species)) targetCode = 'seabass';
    if (!targetCode) return;

    const origin = fcrOrigins.find(o => (o.code || '').toLowerCase() === targetCode);
    if (!origin?.default_version_id) return;
    const currentBelongsToTarget = (origin.versions || []).some(
      v => v.id === formData.fcr_version_id
    );
    if (currentBelongsToTarget) return;

    setFormData(prev => ({ ...prev, fcr_version_id: origin.default_version_id }));
  }, [formData.species, fcrOrigins]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  // Check if current species requires genetic origin
  const isTripletail = (formData.species || '').toLowerCase().includes('tripletail');
  const showGeneticOrigin = !isTilapiaSpecies(formData.species) && !isSeabassSpecies(formData.species) && !isTripletail;

  // Auto-populate species-specific defaults (genetic origin + FCR version)
  // when the user picks a new species. Defined platform-wide via
  // /api/species-defaults so deployed + preview stay in sync.
  useApplySpeciesDefaults({
    token,
    species: formData.species,
    keys: ['genetic_origin', 'fcr_version_id'],
    onApply: (patch) => setFormData((prev) => ({ ...prev, ...patch })),
  });

  // Try-with-Example handler — autofills the form with a curated species
  // preset and triggers the analysis in the same tick. Used by the
  // ModuleExamplesBar above the form. Skipped silently when read-only.
  const loadExampleAndRun = (preset) => {
    if (isReadOnly) return;
    setFormData((prev) => ({ ...prev, ...preset.inputs }));
    toast.success(`Loaded ${preset.species_label} example — running analysis…`);
    // Flag this submit as a curated example run so the POST goes out with
    // `?example=1` and the backend skips quota/credit billing.
    exampleRunRef.current = true;
    // Pin the curated context for the admin "Update Try-X" CTA. We mirror
    // the source record id into activeRecordId so the existing form state
    // is consistent with what the inline-update endpoint will overwrite.
    if (preset?.source_record_id) {
      setActiveRecordId(preset.source_record_id);
      setLoadedCurated({
        module: 'snapshot',
        species: preset.species_label,
        source_record_id: preset.source_record_id,
        current_name: preset.source_record_name || `Try-${preset.species_label} Example`,
      });
    }
    // submit on the next tick so React state has flushed
    setTimeout(() => {
      formRef.current?.requestSubmit?.();
    }, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    
    // Reset missing fish analysis when new analysis is run
    setMissingFishAnalysis(null);
    setShowMissingFishAnalysis(false);
    setFcrDeviationAnalysis(null);
    
    // Validate mandatory fields
    if (!formData.input_date) {
      toast.error('Input Date is required for FCR precision calculations');
      setLoading(false);
      return;
    }
    
    const inputWeight = parseFloat(formData.input_average_weight_g);
    const observedWeight = parseFloat(formData.observed_average_weight_g);
    const isTilapia = isTilapiaSpecies(formData.species);
    const isSeabass = isSeabassSpecies(formData.species);
    
    // Species-specific validation for input_average_weight
    if (isTilapia) {
      // Tilapia: input_average_weight must be at least 20g
      if (!formData.input_average_weight_g || inputWeight < 20) {
        toast.error('For Tilapia, input average weight must be at least 20 g.');
        setLoading(false);
        return;
      }
    } else if (isSeabass) {
      // Seabass: BFCR spreadsheet floor is 3 g; SGR floor is 2 g.
      if (!formData.input_average_weight_g || inputWeight < 2) {
        toast.error('For Seabass, input average weight must be at least 2 g.');
        setLoading(false);
        return;
      }
    } else {
      // Sea Bream: No min/max restriction - any positive value allowed
      if (!formData.input_average_weight_g || inputWeight <= 0) {
        toast.error('Input average weight must be a positive value.');
        setLoading(false);
        return;
      }
    }
    
    // Species-specific validation for observed_average_weight (FCR reference weight)
    if (isTilapia) {
      // Phase B: Tilapia observed_weight must be 200g — 1,400g (weight-indexed BFCR ceiling 1,402g)
      if (!formData.observed_average_weight_g || observedWeight < 200) {
        toast.error('For Tilapia, average weight for analysis must be at least 200 g for reliable FCR calculation.');
        setLoading(false);
        return;
      }
      if (observedWeight > 1400) {
        toast.error('For Tilapia, average weight for analysis must not exceed 1.4 kg (1,400 g).');
        setLoading(false);
        return;
      }
    } else if (isSeabass) {
      // Seabass: observed_weight must be 50g — 600g (BFCR spreadsheet ceiling).
      if (!formData.observed_average_weight_g || observedWeight < 50) {
        toast.error('For Seabass, average weight for analysis must be at least 50 g for reliable FCR comparison.');
        setLoading(false);
        return;
      }
      if (observedWeight > 600) {
        toast.error('For Seabass, average weight for analysis must not exceed 600 g (BFCR spreadsheet ceiling).');
        setLoading(false);
        return;
      }
    } else {
      // Sea Bream: observed_weight must be 200g - 500g for FCR lookup
      if (!formData.observed_average_weight_g || observedWeight < 200) {
        toast.error('For Sea bream, average weight for analysis must be at least 200 g for reliable FCR comparison.');
        setLoading(false);
        return;
      }
      if (observedWeight > 500) {
        toast.error('For Sea bream, average weight for analysis must not exceed 500 g.');
        setLoading(false);
        return;
      }
    }
    
    // Validate genetic origin for non-Tilapia species
    if (showGeneticOrigin && !formData.genetic_origin) {
      toast.error('Genetic origin is required for Seabream');
      setLoading(false);
      return;
    }
    
    // Validate expected fish count is mandatory
    const fishCount = parseInt(formData.expected_fish_count);
    if (!formData.expected_fish_count || isNaN(fishCount) || fishCount <= 0) {
      toast.error('Expected fish count is required to run Snapshot Analysis.');
      setLoading(false);
      return;
    }
    
    try {
      const payload = {
        mode: 'snapshot',
        species: formData.species,
        farm_location: formData.farm_location,
        genetic_origin: showGeneticOrigin ? formData.genetic_origin : null,  // Only include for non-Tilapia
        batch_code: formData.batch_code,
        unit: formData.unit,
        input_date: formData.input_date,
        input_average_weight_g: parseFloat(formData.input_average_weight_g),
        observed_average_weight_g: parseFloat(formData.observed_average_weight_g),
        expected_fish_count: fishCount,
        observed_biological_fcr: parseFloat(formData.observed_biological_fcr),
        // Phase B: pass FCR version id so backend applies the multiplier
        // (Seabream uses genetic_origin + version; Tilapia uses "tilapia" origin + version).
        fcr_version_id: formData.fcr_version_id || null,
      };
      
      // Read + consume the example-run flag so retries / subsequent normal
      // submits don't inherit it (including on error paths).
      const isExampleRun = exampleRunRef.current;
      exampleRunRef.current = false;

      const response = await axios.post(
        `${API}/analysis/snapshot${isExampleRun ? '?example=1' : ''}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'locked') {
        toast.error(response.data.message);
        setResult({ locked: true, message: response.data.message });
      } else {
        setResult(response.data);
        toast.success('Analysis completed successfully!');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        // Pydantic 422 — array of field errors
        msg = detail.map(e => {
          const loc = Array.isArray(e?.loc) ? e.loc.filter(p => p !== 'body').join('.') : '';
          return loc ? `${loc}: ${e?.msg || 'invalid'}` : (e?.msg || 'invalid');
        }).join('; ');
      } else if (typeof detail === 'string') {
        msg = detail;
      } else {
        msg = error.message || 'Analysis failed';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (ratio) => {
    if (Math.abs(ratio - 1.0) < 0.05) return 'text-emerald-400';
    if (ratio > 1.15 || ratio < 0.85) return 'text-red-400';
    return 'text-yellow-400';
  };

  const getSeverityIcon = (ratio) => {
    if (Math.abs(ratio - 1.0) < 0.05) return <CheckCircle className="w-5 h-5" />;
    if (ratio > 1.15 || ratio < 0.85) return <AlertTriangle className="w-5 h-5" />;
    return <AlertCircle className="w-5 h-5" />;
  };

  // Run Missing Fish Analysis
  const runMissingFishAnalysis = () => {
    if (!result || !result.analysis) {
      toast.error('No analysis data available');
      return;
    }

    const analysis = result.analysis;
    const expectedFish = parseInt(formData.expected_fish_count);
    const estimatedFish = analysis.estimated_actual_fish_count;
    const fishDifference = analysis.fish_difference;
    
    // Calculate deviation percentage based on fish count difference
    const deviationPercent = ((estimatedFish - expectedFish) / expectedFish) * 100;
    const roundedDeviation = Math.round(deviationPercent * 10) / 10;
    
    let interpretation = '';
    let arrowType = null; // 'up', 'down', or null
    let interpretationClass = '';
    
    if (deviationPercent < -5) {
      // Estimated fish count is BELOW expected (fewer fish than expected = missing fish)
      interpretation = 'Potential stock discrepancy detected';
      arrowType = 'up';
      interpretationClass = 'text-amber-600';
    } else if (deviationPercent > 5) {
      // Estimated fish count is ABOVE expected (more fish than expected = extra fish)
      interpretation = 'Potentially extra fish in cage';
      arrowType = 'down';
      interpretationClass = 'text-blue-600';
    } else {
      // Within ±5%
      interpretation = 'Within sampling margin of error';
      arrowType = null;
      interpretationClass = 'text-green-600';
    }
    
    const analysisResult = {
      input_date: formData.input_date,
      input_weight_g: parseFloat(formData.input_average_weight_g),
      observed_weight_g: parseFloat(formData.observed_average_weight_g),
      observed_fcr: parseFloat(formData.observed_biological_fcr),
      model_fcr: parseFloat(analysis.model_biological_fcr),
      fcr_ratio: parseFloat(analysis.fcr_ratio),
      expected_fish_count: expectedFish,
      estimated_fish_count: estimatedFish,
      fish_difference: fishDifference,
      deviation_percent: roundedDeviation,
      interpretation,
      arrowType,
      interpretationClass
    };

    // FCR Deviation Classification (Enhanced Analytical Signal)
    const observedFcr = parseFloat(formData.observed_biological_fcr);
    const modelFcr = parseFloat(analysis.model_biological_fcr);
    const fcrClassification = classifySnapshotDeviation(observedFcr, modelFcr);
    
    // ★ Fix: If missing fish signal exists, do NOT classify as biological outperformance
    // Lower FCR when fish are missing is caused by fewer fish consuming the feed, not better biology
    if (fishDifference < 0 && fcrClassification.classification.includes('outperformance')) {
      fcrClassification.classification = 'Possible stock deviation (fewer fish)';
      fcrClassification.interpretation = fcrClassification.interpretation?.replace(
        /outperformance/gi, 'stock deviation'
      );
    }
    
    setFcrDeviationAnalysis(fcrClassification);

    setMissingFishAnalysis(analysisResult);
    setShowMissingFishAnalysis(true);

    // ★ Publish the active SSOT to the global bridge for WingmanChat /
    //   WingmanTriggerPrompts. Snapshot is always single-point:
    //   confidence caps at "Indication Only" and detection is FCR-based only
    //   (no jump/anomaly flags possible on a single observation).
    if (typeof window !== 'undefined') {
      const _absDiff = Math.abs(fishDifference);
      const _direction = fishDifference < 0 ? 'missing'
        : fishDifference > 0 ? 'extra' : 'within_margin';
      const _range = formatMissingFishRange({
        missingFishCount: _absDiff,
        dataReliabilityScore: 35,
        direction: _direction,
      });
      const _hasMissingSignal = _direction !== 'within_margin' && _absDiff > 0;
      const _confidenceLabel = toCanonicalConfidenceLabel(
        CONFIDENCE_LABELS.INDICATION_ONLY, 35, { isSingleSnapshot: true }
      );
      window.__pfd_active_record_ssot = {
        record_name: formData?.unit || null,
        species: formData?.species || null,
        confidenceLabel: _confidenceLabel,
        actionability: _hasMissingSignal ? 'VALIDATION_REQUIRED' : 'MONITOR_ONLY',
        missingFishRangeLabel: _range?.label || null,
        feedingState: null,
        hasConflict: false,
        detectionType: _hasMissingSignal ? 'fcr' : 'none',
        hasAnomaly: false,
        hasMissingFishSignal: _hasMissingSignal,
        moduleName: 'Snapshot',
      };
    }

    toast.success('Missing Fish Analysis complete');
  };

  // Check if there is result data to analyze
  const hasResultData = result && result.analysis && !result.locked;

  // Export to PDF
  const handleExportPdf = async () => {
    if (!result || !result.analysis) {
      toast.error('No analysis results to export');
      return;
    }

    setExportingPdf(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // ===== HEADER SECTION =====
      pdf.setFillColor(8, 145, 178); // Teal
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      pdf.setFontSize(20);
      pdf.setTextColor(255, 255, 255);
      pdf.text('Snapshot Analysis Report', pageWidth / 2, 15, { align: 'center' });
      
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 25, { align: 'center' });
      
      yPos = 45;

      // ===== INPUT PARAMETERS BOX =====
      pdf.setFillColor(248, 250, 252);
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 45, 3, 3, 'FD');
      
      pdf.setFontSize(11);
      pdf.setTextColor(8, 145, 178);
      pdf.setFont(undefined, 'bold');
      pdf.text('Input Parameters', margin + 5, yPos + 8);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      
      const col1X = margin + 5;
      const col2X = pageWidth / 2 + 5;
      
      pdf.text(`Species: ${formData.species}`, col1X, yPos + 18);
      // Genetic origin is meaningful only for Seabream — Tilapia / Seabass / Tripletail have no genetic strain.
      if (showGeneticOrigin) {
        pdf.text(`Genetic Origin: ${formData.genetic_origin}`, col2X, yPos + 18);
      }
      pdf.text(`Input Date: ${formData.input_date}`, col1X, yPos + 25);
      pdf.text(`Input Weight: ${formData.input_average_weight_g}g`, col2X, yPos + 25);
      pdf.text(`Observed Weight: ${formData.observed_average_weight_g}g`, col1X, yPos + 32);
      pdf.text(`Reported FCR: ${formData.observed_biological_fcr}`, col2X, yPos + 32);
      pdf.text(`Expected Fish Count: ${parseInt(formData.expected_fish_count).toLocaleString()}`, col1X, yPos + 39);
      
      yPos += 55;

      // ===== ANALYSIS RESULTS BOX =====
      pdf.setFillColor(240, 253, 244); // Light green
      pdf.setDrawColor(187, 247, 208);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 50, 3, 3, 'FD');
      
      pdf.setFontSize(11);
      pdf.setTextColor(22, 163, 74);
      pdf.setFont(undefined, 'bold');
      pdf.text('Analysis Results', margin + 5, yPos + 8);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      
      // Results in grid format
      pdf.text(`Model FCR: ${result.analysis.model_biological_fcr}`, col1X, yPos + 18);
      pdf.text(`FCR Gap: ${result.analysis.fcr_gap > 0 ? '+' : ''}${result.analysis.fcr_gap}`, col2X, yPos + 18);
      pdf.text(`FCR Ratio: ${result.analysis.fcr_ratio}`, col1X, yPos + 25);
      pdf.text(`Estimated Actual Fish: ${result.analysis.estimated_actual_fish_count.toLocaleString()}`, col2X, yPos + 25);
      
      // Fish difference with color
      const fishDiff = result.analysis.fish_difference;
      pdf.setFont(undefined, 'bold');
      if (fishDiff > 0) {
        pdf.setTextColor(22, 163, 74); // Green
      } else if (fishDiff < 0) {
        pdf.setTextColor(220, 38, 38); // Red
      }
      pdf.text(`Fish Count Difference: ${fishDiff > 0 ? '+' : ''}${fishDiff} fish`, col1X, yPos + 35);
      
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(55, 65, 81);
      
      yPos += 60;

      // ===== INTERPRETATION BOX =====
      pdf.setFillColor(239, 246, 255); // Light blue
      pdf.setDrawColor(191, 219, 254);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 25, 3, 3, 'FD');
      
      pdf.setFontSize(10);
      pdf.setTextColor(30, 64, 175);
      pdf.setFont(undefined, 'bold');
      pdf.text('Interpretation', margin + 5, yPos + 8);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      
      const interpretationLines = pdf.splitTextToSize(result.analysis.interpretation, pageWidth - (margin * 2) - 10);
      pdf.text(interpretationLines, margin + 5, yPos + 15);
      
      yPos += 35;

      // ===== SNAPSHOT WARNING BOX =====
      pdf.setFillColor(255, 251, 235); // Light amber
      pdf.setDrawColor(251, 191, 36); // Amber border
      pdf.setLineWidth(0.5);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 40, 3, 3, 'FD');
      
      pdf.setFontSize(9);
      pdf.setTextColor(146, 64, 14); // Amber-800
      pdf.setFont(undefined, 'bold');
      pdf.text('⚠ Important: This result is based on a single data point and should be treated as an initial indication only.', margin + 5, yPos + 10);
      
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(180, 83, 9); // Amber-700
      pdf.text('For validation and higher confidence, upload your full production data to the Spreadsheet Uploader.', margin + 5, yPos + 20);
      
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(146, 64, 14);
      pdf.text('Do not make stock or feeding adjustments based on this result alone.', margin + 5, yPos + 30);
      
      pdf.setLineWidth(0.2); // Reset line width
      yPos += 50;

      // ===== MISSING FISH ANALYSIS (if run) =====
      if (showMissingFishAnalysis && missingFishAnalysis) {
        if (yPos > pageHeight - 70) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFillColor(250, 245, 255); // Light purple
        pdf.setDrawColor(216, 180, 254);
        pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 55, 3, 3, 'FD');
        
        pdf.setFontSize(11);
        pdf.setTextColor(147, 51, 234);
        pdf.setFont(undefined, 'bold');
        pdf.text('Missing Fish Analysis Summary', margin + 5, yPos + 8);
        
        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(55, 65, 81);
        
        // Table headers
        const tableY = yPos + 15;
        const mfaColWidths = [30, 25, 25, 30, 30, 35];
        const mfaHeaders = ['Input Date', 'Obs FCR', 'Model FCR', 'Expected', 'Estimated', 'Difference'];
        
        pdf.setFillColor(147, 51, 234);
        pdf.rect(margin + 3, tableY, pageWidth - (margin * 2) - 6, 6, 'F');
        
        pdf.setFontSize(7);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, 'bold');
        
        let xPos = margin + 5;
        mfaHeaders.forEach((header, i) => {
          pdf.text(header, xPos, tableY + 4);
          xPos += mfaColWidths[i];
        });
        
        // Table row
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(55, 65, 81);
        const rowY = tableY + 10;
        xPos = margin + 5;
        
        pdf.text(missingFishAnalysis.input_date || 'N/A', xPos, rowY);
        xPos += mfaColWidths[0];
        
        pdf.text(missingFishAnalysis.observed_fcr?.toFixed(3) || 'N/A', xPos, rowY);
        xPos += mfaColWidths[1];
        
        pdf.text(missingFishAnalysis.model_fcr?.toFixed(3) || 'N/A', xPos, rowY);
        xPos += mfaColWidths[2];
        
        pdf.text(missingFishAnalysis.expected_fish_count?.toLocaleString() || 'N/A', xPos, rowY);
        xPos += mfaColWidths[3];
        
        pdf.text(missingFishAnalysis.estimated_fish_count?.toLocaleString() || 'N/A', xPos, rowY);
        xPos += mfaColWidths[4];
        
        // Color code difference
        const diff = missingFishAnalysis.fish_difference;
        if (diff < 0) {
          pdf.setTextColor(217, 119, 6); // Amber
        } else if (diff > 0) {
          pdf.setTextColor(37, 99, 235); // Blue
        } else {
          pdf.setTextColor(22, 163, 74); // Green
        }
        pdf.setFont(undefined, 'bold');
        pdf.text(`${diff > 0 ? '+' : ''}${diff?.toLocaleString() || 'N/A'}`, xPos, rowY);
        
        // Interpretation
        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(55, 65, 81);
        pdf.text(`Interpretation: ${missingFishAnalysis.interpretation}`, margin + 5, rowY + 8);
        pdf.text(`Deviation: ${missingFishAnalysis.deviation_percent > 0 ? '+' : ''}${missingFishAnalysis.deviation_percent}%`, margin + 5, rowY + 14);
      }

      // ===== FOOTER =====
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Precision Fish Detection - Aquaculture Analysis Tool | Page ${i} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      // Save PDF
      const fileName = `snapshot_analysis_${formData.species.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200" data-testid="snapshot-analysis-page">
      <Header />
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/')}
            data-testid="back-button"
            variant="outline"
            className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#0891b2]">Snapshot Analysis</h1>
            <p className="text-gray-600">Signal tool — quick stock deviation screening based on reported FCR performance.</p>
          </div>
        </div>

        {/* Try-with-Example bar — prominent CTA for first-time users */}
        <ModuleExamplesBar
          examples={snapshotExamples}
          onSelect={loadExampleAndRun}
          moduleLabel="Snapshot Analysis"
          isReadOnly={isReadOnly}
        />

        {/* ★ Above-the-fold feature video — PRD item 1 */}
        <FeatureVideoHero
          featureName="Snapshot Analysis"
          youtubeUrl={HELP_URLS?.snapshotAnalysis?.youtube}
        />

        <Card className="glass-card border-slate-200" data-testid="snapshot-form-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-[#0891b2]">
              <Camera className="w-5 h-5 text-[#0891b2]" />
              <span>Input Parameters</span>
            </CardTitle>
            <CardDescription className="text-gray-600">Enter your cage data for analysis</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Read-Only Mode Banner */}
            {isReadOnly && (
              <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg" data-testid="read-only-banner">
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">Read-Only Mode</p>
                    <p className="text-xs text-amber-700">Your account is pending approval. You can explore the interface but cannot run analyses or fill in fields.</p>
                  </div>
                </div>
              </div>
            )}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
              <fieldset disabled={isReadOnly} className={isReadOnly ? 'opacity-60' : ''}>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="species">Species</Label>
                  <Select
                    value={formData.species}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, species: value }))}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="bg-white border-slate-300 focus:border-[#0891b2]" data-testid="species-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectGroup>
                        <SelectLabel className="text-[#0891b2] font-semibold">Available Species</SelectLabel>
                        {SUPPORTED_SPECIES.map(species => (
                          <SelectItem key={species.value} value={species.value} data-testid={`species-option-${species.value.toLowerCase()}`}>
                            {species.label}
                          </SelectItem>
                        ))}
                        {userSpecies.filter(s => s.value === 'Tripletail').map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-gray-400 font-semibold">Coming Soon</SelectLabel>
                        {COMING_SOON_SPECIES.map(species => (
                          <SelectItem 
                            key={species.value} 
                            value={species.value} 
                            disabled 
                            className="text-gray-400 italic"
                          >
                            {species.label} (Coming soon)
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="farm_location">Farm Location</Label>
                  <Input
                    id="farm_location"
                    name="farm_location"
                    data-testid="farm-location-input"
                    placeholder="e.g., Norway Coast"
                    value={formData.farm_location}
                    onChange={handleChange}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                </div>
                
                {/* Genetic Origin - Only shown for non-Tilapia species */}
                {showGeneticOrigin && (
                  <div className="space-y-2">
                    <Label htmlFor="genetic_origin">Genetic Origin <span className="text-red-500">*</span></Label>
                    <select
                      id="genetic_origin"
                      name="genetic_origin"
                      data-testid="genetic-origin-select"
                      value={formData.genetic_origin}
                      onChange={handleChange}
                      required
                      className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#0891b2] focus:outline-none"
                    >
                      <option value="Greek">Greek</option>
                      <option value="French">French</option>
                      <option value="Italian">Italian</option>
                    </select>
                  </div>
                )}

                {/* FCR Version selector — available for ALL species. Maps
                    (species, genetic_origin) → origin_code so Seabream picks
                    a Greek/French/Italian version, Tilapia/Seabass/Tripletail
                    pick their species-specific version. Admin manages versions
                    in Admin Console → FCR Origin Versions. */}
                {fcrOrigins.length > 0 && (() => {
                  let code;
                  let label;
                  if (isTilapiaSpecies(formData.species)) { code = 'tilapia'; label = 'Tilapia'; }
                  else if (isSeabassSpecies(formData.species)) { code = 'seabass'; label = 'Seabass'; }
                  else if ((formData.species || '').toLowerCase().includes('tripletail')) { code = 'tripletail'; label = 'Tripletail'; }
                  else { code = (formData.genetic_origin || 'greek').toLowerCase(); label = `Seabream · ${(formData.genetic_origin || 'Greek')}`; }
                  const origin = fcrOrigins.find(o => (o.code || '').toLowerCase() === code);
                  const versions = origin?.versions || [];
                  if (versions.length === 0) {
                    return (
                      <div className="space-y-2">
                        <Label>FCR Version <span className="text-xs text-gray-500">({label})</span></Label>
                        <div
                          className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
                          data-testid="snapshot-fcr-version-none"
                        >
                          No {label} FCR versions configured yet — using admin spreadsheet baseline (multiplier = 1.00×).
                        </div>
                        <FCRVersionGuidanceNote />
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      <Label htmlFor="fcr_version_id">
                        FCR Version <span className="text-xs text-gray-500">({label})</span>
                      </Label>
                      <select
                        id="fcr_version_id"
                        name="fcr_version_id"
                        data-testid="snapshot-fcr-version-select"
                        value={formData.fcr_version_id || ''}
                        onChange={handleChange}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900 focus:border-[#0891b2]"
                      >
                        {versions.map(v => (
                          <option key={v.id} value={v.id}>
                            {v.label || `${v.version_name} · ${v.multiplier_percent ?? 100}%`}
                            {origin?.default_version_id === v.id ? ' (default)' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-500">
                        Manage {label} FCR versions under Admin Console → FCR Origin Versions.
                      </p>
                      <FCRVersionGuidanceNote />
                    </div>
                  );
                })()}
                
                <div className="space-y-2">
                  <Label htmlFor="batch_code">Batch Code</Label>
                  <Input
                    id="batch_code"
                    name="batch_code"
                    data-testid="batch-code-input"
                    placeholder="e.g., BATCH-2025-001"
                    value={formData.batch_code}
                    onChange={handleChange}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Input
                    id="unit"
                    name="unit"
                    data-testid="unit-input"
                    placeholder="e.g., Cage A1"
                    value={formData.unit}
                    onChange={handleChange}
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="input_date">
                    Input Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="input_date"
                    name="input_date"
                    data-testid="input-date"
                    type="date"
                    value={formData.input_date}
                    onChange={handleChange}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                  <p className="text-xs text-gray-500">Date the snapshot data is valid for</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="input_average_weight_g">
                    Input Average Weight (g) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="input_average_weight_g"
                    name="input_average_weight_g"
                    data-testid="weight-input"
                    type="number"
                    step="0.1"
                    min={isTilapiaSpecies(formData.species) ? "20" : (isSeabassSpecies(formData.species) ? "2" : "0.1")}
                    placeholder={isTilapiaSpecies(formData.species) ? "Min 20g" : (isSeabassSpecies(formData.species) ? "Min 2g" : "Any positive value")}
                    value={formData.input_average_weight_g}
                    onChange={handleChange}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                  <p className="text-xs text-gray-500">
                    {isTilapiaSpecies(formData.species)
                      ? "For Tilapia: Min 20g (no upper limit)"
                      : isSeabassSpecies(formData.species)
                      ? "For Seabass: Min 2g (SGR floor)"
                      : "For Sea Bream: Any positive value (no restrictions)"}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="observed_average_weight_g">
                    Average Weight for Analysis (g) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="observed_average_weight_g"
                    name="observed_average_weight_g"
                    data-testid="observed-weight-input"
                    type="number"
                    step="0.1"
                    min={isSeabassSpecies(formData.species) ? "50" : "200"}
                    max={isTilapiaSpecies(formData.species) ? "1400" : (isSeabassSpecies(formData.species) ? "600" : "500")}
                    placeholder={isTilapiaSpecies(formData.species) ? "200 - 1400" : (isSeabassSpecies(formData.species) ? "50 - 600" : "200 - 500")}
                    value={formData.observed_average_weight_g}
                    onChange={handleChange}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                  <p className="text-xs text-gray-500">
                    {isTilapiaSpecies(formData.species)
                      ? "For Tilapia: 200g - 1.4 kg (FCR reference)"
                      : isSeabassSpecies(formData.species)
                      ? "For Seabass: 50g - 600g (admin BFCR spreadsheet)"
                      : "For Sea Bream: 200g - 500g (FCR reference)"}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="expected_fish_count">
                    Expected Fish Count <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="expected_fish_count"
                    name="expected_fish_count"
                    data-testid="fish-count-input"
                    type="number"
                    min="1"
                    placeholder="e.g., 10000"
                    value={formData.expected_fish_count}
                    onChange={handleChange}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                  <p className="text-xs text-gray-500">Required for missing fish calculation</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="observed_biological_fcr">Reported FCR</Label>
                  <Input
                    id="observed_biological_fcr"
                    name="observed_biological_fcr"
                    data-testid="observed-fcr-input"
                    type="number"
                    step="0.001"
                    placeholder="e.g., 1.25"
                    value={formData.observed_biological_fcr}
                    onChange={handleChange}
                    required
                    className="bg-white border-slate-300 focus:border-[#0891b2]"
                  />
                </div>
                
              </div>
              
              <Button
                type="submit"
                data-testid="analyze-button"
                className="w-full bg-[#0891b2] hover:bg-[#06748c] text-white"
                disabled={loading || isReadOnly}
              >
                {isReadOnly ? (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Pending Approval
                  </>
                ) : loading ? 'Analyzing...' : `Run Analysis${formatCostLabel('snapshot')}`}
              </Button>
              <div className="flex justify-center pt-2">
                <UpdateCuratedExampleButton
                  module="snapshot"
                  species={loadedCurated?.species}
                  currentSpecies={formData.species}
                  token={token}
                  isAdmin={!!user?.is_admin}
                  sourceRecordId={loadedCurated?.source_record_id}
                  currentRecordId={activeRecordId}
                  currentName={loadedCurated?.current_name}
                  buildRecordPatch={() => ({
                    species: formData.species,
                    form_data: formData,
                    name: loadedCurated?.current_name,
                  })}
                  onUpdated={() => refetchSnapshotExamples?.()}
                />
              </div>
              </fieldset>
            </form>
          </CardContent>
        </Card>

        {result && result.locked && (
          <Card className="glass-card border-red-200 animate-fade-in bg-red-50" data-testid="locked-card">
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Analysis Quota Reached</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">{result.message}</p>
            </CardContent>
          </Card>
        )}

        {result && !result.locked && (
          <Card className="glass-card border-slate-200 animate-fade-in" data-testid="results-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#0891b2]">Analysis Results</CardTitle>
              {user?.is_admin && (
              <Button
                onClick={handleExportPdf}
                disabled={exportingPdf}
                variant="outline"
                size="sm"
                className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportingPdf ? 'Exporting...' : 'Export PDF'}
              </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-gray-600 mb-1">Model FCR</p>
                  <p className="text-xl font-bold text-[#0891b2]" data-testid="model-fcr-value">
                    {result.analysis.model_biological_fcr}
                  </p>
                  {/* Phase B: FCR version badge (shows which origin+version
                      shaped the Model FCR — renders for Tilapia and Seabream
                      whenever the backend returned a known version). */}
                  {result.analysis.fcr_origin_code && (
                    <p
                      className="mt-1 text-[11px] text-slate-500"
                      data-testid="snapshot-fcr-version-badge"
                    >
                      {(() => {
                        const code = (result.analysis.fcr_origin_code || '').toLowerCase();
                        if (code === 'tilapia') return 'Tilapia';
                        if (code === 'seabass') return 'Seabass';
                        return code ? code.charAt(0).toUpperCase() + code.slice(1) : '';
                      })()}
                      {result.analysis.fcr_version_name ? ` · ${result.analysis.fcr_version_name}` : ''}
                      {' '}({(result.analysis.fcr_multiplier_percent ?? 100)}%)
                    </p>
                  )}
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-gray-600 mb-1">FCR Gap</p>
                  <p className="text-xl font-bold text-[#0891b2]" data-testid="fcr-gap-value">
                    {result.analysis.fcr_gap > 0 ? '+' : ''}{result.analysis.fcr_gap}
                  </p>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-gray-600 mb-1">FCR Ratio</p>
                  <p className={`text-xl font-bold ${getSeverityColor(result.analysis.fcr_ratio)}`} data-testid="fcr-ratio-value">
                    {result.analysis.fcr_ratio}
                  </p>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-gray-600 mb-1">Estimated Actual Fish</p>
                  <p className="text-xl font-bold text-[#0891b2]" data-testid="estimated-fish-value">
                    ~{result.analysis.estimated_actual_fish_count.toLocaleString()}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Single snapshot — treat as indicative</p>
                </div>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-start space-x-3">
                  <div className={getSeverityColor(result.analysis.fcr_ratio)}>
                    {getSeverityIcon(result.analysis.fcr_ratio)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-2 text-gray-800">Fish Count Difference</p>
                    {(() => {
                      // ★ ITEM 3+6 — canonical "Expected Value + Confidence Range (±10%)" shape.
                      //   Uses the shared reportFormatters helper so Snapshot, Spreadsheet, PDF
                      //   and Wingman all render the same band widths for the same count.
                      const diff = result.analysis.fish_difference;
                      const absDiff = Math.abs(diff);
                      const expected = parseInt(formData.expected_fish_count) || 0;
                      const pct = expected > 0
                        ? Math.abs(diff / expected * 100).toFixed(1)
                        : '?';
                      const direction = diff < 0 ? 'missing' : 'extra';
                      const directionColor = diff < 0 ? 'text-amber-700' : 'text-blue-700';
                      if (absDiff === 0) {
                        return (
                          <p className="text-xl font-bold text-green-600" data-testid="fish-difference-value">
                            Within margin
                          </p>
                        );
                      }
                      const range = formatMissingFishRange({
                        missingFishCount: absDiff,
                        dataReliabilityScore: 35,
                        direction,
                        allowWidening: true,
                      });
                      return (
                        <>
                          <p className={`text-xl font-bold ${directionColor}`} data-testid="fish-difference-value">
                            Expected {direction} fish: ~{range.midpointLabel}
                          </p>
                          <p className="text-sm text-slate-600 mt-1" data-testid="fish-difference-range">
                            Confidence Range (±{range.uncertaintyMarginPercent || 15}%): {range.shortLabel} fish
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            ~{pct}% of declared count. Wide range → low reliability; narrow range → high reliability.
                          </p>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm font-semibold mb-2 text-gray-800">Interpretation</p>
                {(() => {
                  const diff = result.analysis.fish_difference;
                  const absDiff = Math.abs(diff);
                  const expected = parseInt(formData.expected_fish_count) || 1;
                  const devPct = Math.abs(diff / expected * 100).toFixed(1);

                  if (devPct <= 5) {
                    return <p className="text-sm text-green-700" data-testid="interpretation-text">
                      Fish count is within the ±5% sampling margin — no signal detected from this single snapshot.
                    </p>;
                  }
                  const range = formatMissingFishRange({
                    missingFishCount: absDiff,
                    dataReliabilityScore: 35,
                    direction: diff < 0 ? 'missing' : 'extra',
                    allowWidening: true,
                  });
                  if (diff < 0) {
                    return <p className="text-sm text-amber-700" data-testid="interpretation-text">
                      Possible missing fish signal: expected ~{range.midpointLabel} fish (range {range.shortLabel}), ~{devPct}% below declared count. Single snapshot — confirm with a follow-up sample before any action.
                    </p>;
                  }
                  return <p className="text-sm text-blue-700" data-testid="interpretation-text">
                    FCR suggests extra fish: expected ~{range.midpointLabel} (range {range.shortLabel}) more than declared, ~{devPct}% above declared count. Single snapshot — confirm with a follow-up sample.
                  </p>;
                })()}
              </div>
              
              {/* ★ SNAPSHOT WARNING — Always visible for every result */}
              <div className="p-4 bg-amber-50 border-2 border-amber-400 rounded-lg" data-testid="snapshot-warning-box">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-amber-800">
                      Important: This result is based on a single data point and should be treated as an initial indication only.
                    </p>
                    <p className="text-sm text-amber-700">
                      For validation and higher confidence, upload your full production data to the{' '}
                      <a href="/spreadsheet" className="font-semibold underline hover:text-amber-900">
                        Spreadsheet Uploader
                      </a>.
                    </p>
                    <p className="text-sm font-semibold text-amber-800">
                      Do not make stock or feeding adjustments based on this result alone.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Run Missing Fish Analysis Button */}
              <div className="mt-6 flex justify-center">
                <Button
                  onClick={runMissingFishAnalysis}
                  disabled={!hasResultData}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-sm"
                  data-testid="run-missing-fish-analysis-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Run Missing Fish Analysis
                </Button>
              </div>

              {/* Missing Fish Analysis Results */}
              {showMissingFishAnalysis && missingFishAnalysis && (
                <div className="mt-6 space-y-4" data-testid="missing-fish-analysis-results">
                  <h3 className="text-base font-semibold text-purple-800 flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    Missing Fish Analysis Summary
                  </h3>
                  
                  {/* ★ DECISION CONTEXT PANEL — Structured decision support at TOP */}
                  <SnapshotDecisionPanel 
                    missingFishAnalysis={missingFishAnalysis} 
                    fcrDeviationAnalysis={fcrDeviationAnalysis} 
                  />

                  {/* PRD final-pass UX simplification (#7): WingmanTriggerPrompts
                      removed from Snapshot. Per the agreed CTA-reduction plan,
                      contextual "Ask Wingman" prompt pills live ONLY near the
                      primary Growth-vs-Model chart (Spreadsheet + Growth Model
                      pages). Snapshot has no growth chart, so the prompts are
                      not surfaced here — the floating WingmanChat panel and
                      the WingmanInsight collapsible at the bottom remain
                      available CTAs. */}
                  
                  {/* Stock Integrity Score - displays when FCR deviation is calculated */}
                  {fcrDeviationAnalysis && (
                    <SnapshotStockIntegrity fcrDeviationAnalysis={fcrDeviationAnalysis} />
                  )}
                  
                  {/* Analysis Indicator */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-700 mb-1 font-medium">Signal Indicator</p>
                    <p className="text-xs text-purple-500 mb-3">Based on your reported biological FCR — not validated against daily feed records. Use as a directional signal, not a confirmed finding.</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div 
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                          missingFishAnalysis.arrowType === 'up' 
                            ? 'bg-amber-50 border-amber-300' 
                            : missingFishAnalysis.arrowType === 'down' 
                              ? 'bg-blue-50 border-blue-300' 
                              : 'bg-green-50 border-green-300'
                        }`}
                        title={`${missingFishAnalysis.interpretation}\nDeviation: ${missingFishAnalysis.deviation_percent > 0 ? '+' : ''}${missingFishAnalysis.deviation_percent}%`}
                      >
                        <span className="text-sm font-medium">Snapshot</span>
                        {missingFishAnalysis.arrowType === 'up' && (
                          <ArrowUpCircle className="w-5 h-5 text-amber-600" />
                        )}
                        {missingFishAnalysis.arrowType === 'down' && (
                          <ArrowDownCircle className="w-5 h-5 text-blue-600" />
                        )}
                        {!missingFishAnalysis.arrowType && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        <span className={`text-sm ${missingFishAnalysis.interpretationClass}`}>
                          {missingFishAnalysis.deviation_percent > 0 ? '+' : ''}{missingFishAnalysis.deviation_percent}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600 px-2">
                    <div className="flex items-center gap-1">
                      <ArrowUpCircle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm">Missing fish signal</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ArrowDownCircle className="w-4 h-4 text-blue-600" />
                      <span className="text-sm">Extra fish signal</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm">Within margin (±5%)</span>
                    </div>
                  </div>

                  {/* Summary Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse" data-testid="missing-fish-table">
                      <thead>
                        <tr className="bg-purple-100">
                          <th className="border border-purple-300 px-2 md:px-3 py-2 text-left text-sm font-semibold">Input Date</th>
                          <th className="border border-purple-300 px-2 md:px-3 py-2 text-right text-sm font-semibold">Reported FCR</th>
                          <th className="border border-purple-300 px-2 md:px-3 py-2 text-right text-sm font-semibold">Model FCR</th>
                          <th className="border border-purple-300 px-2 md:px-3 py-2 text-right text-sm font-semibold">Expected Fish</th>
                          <th className="border border-purple-300 px-2 md:px-3 py-2 text-right text-sm font-semibold">Estimated Fish</th>
                          <th className="border border-purple-300 px-2 md:px-3 py-2 text-right text-sm font-semibold">Difference</th>
                          <th className="border border-purple-300 px-2 md:px-3 py-2 text-left text-sm font-semibold">Interpretation</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="hover:bg-purple-50">
                          <td className="border border-purple-200 px-2 md:px-3 py-2 text-sm">
                            {missingFishAnalysis.input_date}
                          </td>
                          <td className="border border-purple-200 px-2 md:px-3 py-2 text-right text-sm font-medium">
                            {missingFishAnalysis.observed_fcr.toFixed(3)}
                          </td>
                          <td className="border border-purple-200 px-2 md:px-3 py-2 text-right text-sm">
                            {missingFishAnalysis.model_fcr.toFixed(3)}
                          </td>
                          <td className="border border-purple-200 px-2 md:px-3 py-2 text-right text-sm">
                            {missingFishAnalysis.expected_fish_count.toLocaleString()}
                          </td>
                          <td className="border border-purple-200 px-2 md:px-3 py-2 text-right text-sm font-medium">
                            {missingFishAnalysis.estimated_fish_count.toLocaleString()}
                          </td>
                          <td className={`border border-purple-200 px-2 md:px-3 py-2 text-right text-sm font-semibold ${
                            missingFishAnalysis.fish_difference < 0 ? 'text-amber-600' : 
                            missingFishAnalysis.fish_difference > 0 ? 'text-blue-600' : 'text-green-600'
                          }`}>
                            {(() => {
                              const d = Math.abs(missingFishAnalysis.fish_difference);
                              if (d === 0) return 'Within margin';
                              const lo = Math.max(0, Math.round(d * 0.7));
                              const hi = Math.round(d * 1.3);
                              const label = missingFishAnalysis.fish_difference < 0 ? 'Missing' : 'Extra';
                              return `${label}: ${lo.toLocaleString()}–${hi.toLocaleString()}`;
                            })()}
                          </td>
                          <td className="border border-purple-200 px-2 md:px-3 py-2 text-sm">
                            <div className="flex items-center gap-2">
                              {missingFishAnalysis.arrowType === 'up' && (
                                <ArrowUpCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                              )}
                              {missingFishAnalysis.arrowType === 'down' && (
                                <ArrowDownCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              )}
                              {!missingFishAnalysis.arrowType && (
                                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                              )}
                              <span className={`text-sm ${missingFishAnalysis.interpretationClass}`}>
                                {missingFishAnalysis.interpretation}
                              </span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Causes/explanations intentionally omitted — single snapshot cannot establish causation */}

                  {/* FCR Deviation Classification - Enhanced Analytical Signal */}
                  {fcrDeviationAnalysis && (
                    <div className="mt-4 space-y-3 border-t border-purple-200 pt-4" data-testid="fcr-deviation-classification">
                      <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        FCR Deviation Analysis
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* FCR Classification */}
                        <div className={`border rounded-lg p-3 ${
                          fcrDeviationAnalysis.classification.includes('stock deviation') 
                            ? 'bg-red-50 border-red-200' 
                            : fcrDeviationAnalysis.classification.includes('outperformance') || fcrDeviationAnalysis.classification.includes('uncertainty')
                              ? 'bg-green-50 border-green-200'
                              : 'bg-amber-50 border-amber-200'
                        }`}>
                          <p className="text-xs font-semibold text-gray-700 mb-1">Classification</p>
                          <p className={`text-sm font-bold ${
                            fcrDeviationAnalysis.classification.includes('stock deviation') 
                              ? 'text-red-700' 
                              : fcrDeviationAnalysis.classification.includes('outperformance') || fcrDeviationAnalysis.classification.includes('uncertainty')
                                ? 'text-green-700'
                                : 'text-amber-700'
                          }`}>{fcrDeviationAnalysis.classification}</p>
                          <p className="text-xs text-gray-500 mt-1">FCR Deviation: {fcrDeviationAnalysis.deviation > 0 ? '+' : ''}{fcrDeviationAnalysis.deviation}%</p>
                        </div>

                        {/* Deviation Magnitude — renamed from "Signal Strength" so the
                            single-snapshot reliability story isn't contradicted by an
                            authoritative-sounding "Strong / Moderate / Weak" verdict.
                            Magnitude describes the SIZE of the FCR deviation only;
                            confidence in the underlying signal is always "Indicative"
                            on a single sample (see Stock Integrity Score for the
                            reliability dimension). */}
                        <div className={`border rounded-lg p-3 ${
                          fcrDeviationAnalysis.signalStrength === 'Strong' 
                            ? 'bg-red-50 border-red-200' 
                            : fcrDeviationAnalysis.signalStrength === 'Moderate'
                              ? 'bg-amber-50 border-amber-200'
                              : 'bg-green-50 border-green-200'
                        }`}>
                          <p className="text-xs font-semibold text-gray-700 mb-1">Deviation Magnitude</p>
                          <p className={`text-lg font-bold ${
                            fcrDeviationAnalysis.signalStrength === 'Strong' 
                              ? 'text-red-700' 
                              : fcrDeviationAnalysis.signalStrength === 'Moderate'
                                ? 'text-amber-700'
                                : 'text-green-700'
                          }`}>{
                            // Map the legacy Strong/Moderate/Weak labels to size words
                            // so the column reads as a magnitude, not a confidence.
                            { Strong: 'Large', Moderate: 'Medium', Weak: 'Small' }[fcrDeviationAnalysis.signalStrength]
                              || fcrDeviationAnalysis.signalStrength
                          }</p>
                          <p className="text-xs text-gray-500 mt-1">Absolute deviation: {fcrDeviationAnalysis.absDeviation}%</p>
                          <p className="text-xs text-gray-400 mt-1 italic">Single-snapshot — confidence is always Indicative.</p>
                        </div>
                      </div>

                      {/* Interpretation */}
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-purple-700 mb-1">Analytical Interpretation</p>
                        <p className="text-sm text-purple-800">{fcrDeviationAnalysis.interpretation}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <WingmanNudge patterns={(() => {
                const p = [];
                const dev = result.analysis?.fcr_deviation_percent;
                if (dev && Math.abs(dev) > 15) p.push({ id: 'fcr-deviation', label: `FCR deviates ${Math.abs(dev).toFixed(0)}% from expected — review feeding efficiency`, severity: Math.abs(dev) > 30 ? 'alert' : 'warning' });
                const ratio = result.analysis?.fcr_ratio;
                if (ratio && ratio < 0.8) p.push({ id: 'low-fcr-ratio', label: `FCR ratio of ${ratio.toFixed(2)} suggests possible missing fish`, severity: 'alert' });
                return p;
              })()} />

              <WingmanInsight 
                analysisType="Snapshot FCR Analysis" 
                analysisData={{
                  species: result.analysis?.species,
                  actual_fcr: result.analysis?.actual_biological_fcr,
                  expected_fcr: result.analysis?.expected_biological_fcr,
                  fcr_ratio: result.analysis?.fcr_ratio,
                  temperature: result.analysis?.temperature,
                  avg_weight: result.analysis?.avg_weight_grams,
                  fish_count: result.analysis?.expected_fish_count,
                  // Pass the missing-fish signal explicitly so the AI applies
                  // the LOW-FCR / MISSING-FISH contradiction rule (no longer
                  // celebrating a low FCR as efficient when stock is short).
                  estimated_fish_count: missingFishAnalysis?.estimated_fish_count,
                  fish_difference: missingFishAnalysis?.fish_difference,
                  missing_fish_signal_active: (missingFishAnalysis?.fish_difference || 0) < 0,
                  fcr_deviation_classification: fcrDeviationAnalysis?.classification,
                  deviation: result.analysis?.fcr_deviation_percent
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        {result && !result.locked && !isDemo && (
          <Card className="glass-card border-[#0891b2]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Record name (optional)"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  className="flex-1 bg-white"
                  data-testid="snapshot-save-name"
                />
                <Button onClick={handleSave} disabled={saving} className="bg-[#0891b2] hover:bg-[#06748c] text-white" data-testid="snapshot-save-btn">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Glossary footer (collapsible) — visible on every Snapshot result */}
        {result && !result.locked && (
          <ReportGlossary />
        )}

        {/* Saved Records Section */}
        <Collapsible open={showSaved} onOpenChange={setShowSaved}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-testid="snapshot-saved-toggle">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-[#0891b2]" />
                <span className="font-medium text-gray-700 text-sm">Saved Snapshots ({savedRecords.length})</span>
              </div>
              {showSaved ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            {savedRecords.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-4">No saved snapshots yet</p>
            ) : (
              <div className="space-y-2">
                {snapshotSortFilterBar}
                {filteredSnapshotRecords.map(record => (
                  <div key={record.id} className={`flex items-center justify-between p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50 transition ${activeRecordId === record.id ? 'border-[#0891b2] bg-[#0891b2]/5' : 'border-gray-200'}`} data-testid={`snapshot-record-${record.id}`}>
                    <div className="flex-1 min-w-0" onClick={() => loadRecord(record)}>
                      <div className="text-sm font-medium text-gray-800">
                        <InlineRename
                          value={record.name}
                          onSave={(newName) => renameSnapshotRecord(record.id, newName)}
                          disabled={isDemo}
                        />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(record.created_at).toLocaleDateString()}</span>
                        {record.result?.analysis && (
                          <>
                            <span>|</span>
                            <span>Fish diff: {record.result.analysis.fish_difference > 0 ? '+' : ''}{record.result.analysis.fish_difference}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {!isDemo && (
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 ml-2" onClick={() => confirmDelete(record.id)} data-testid={`snapshot-delete-${record.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Admin-only: Save / Discard report-visibility changes */}
        <AdminHiddenSectionsOverview className="mb-4" />

        {/* Help & Benefits Section */}
        <HelpBenefits 
          youtubeUrl={HELP_URLS.snapshotAnalysis.youtube}
          benefitsUrl={HELP_URLS.snapshotAnalysis.benefits}
          featureName="Snapshot Analysis"
        />
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Snapshot Record</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this saved snapshot? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}